/**
 * Adapted from: Waza
 * Source: https://github.com/tw93/Waza
 * Source commit: ac1cc9d685718996df081d70e677d527c3ff981f
 * License: MIT
 * Modified for: Smart Shikka Core PR79 selective source adoption.
 * Safety: metadata/preview only; no runtime execution.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SOURCE_REPO_URL = "https://github.com/tw93/Waza";
const SOURCE_COMMIT_SHA = "ac1cc9d685718996df081d70e677d527c3ff981f";
const SOURCE_LICENSE = "MIT";
const COMMON_BLOCKED_ACTIONS = deepFreeze([
    "run_source_repo_script",
    "install_source_repo_dependencies",
    "execute_workflow",
    "network_call",
    "browser_automation",
    "desktop_automation",
    "read_secret",
    "db_write",
    "supabase_call",
    "provider_call",
    "copy_full_repo",
    "vendor_full_repo",
    "copy_forbidden_scripts",
    "copy_pake_source",
    "run_pake_command",
    "auto_save",
    "auto_delete",
    "payment_trigger",
    "role_mutation",
    "secret_mutation",
]);
// ---------------------------------------------------------------------------
// Skill definitions
// ---------------------------------------------------------------------------
const WAZA_SKILLS = deepFreeze([
    {
        id: "think",
        displayName: "Smart Shikka AI workflow skill: Think",
        description: "Turns rough ideas into approved, decision-complete plans before coding.",
        skillFileName: "think-SKILL.md",
        relativePath: "packages/core/src/waza-workflow-skills/references/think-SKILL.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        planOnly: true,
        runtimeExecutionAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        referenceFiles: [],
        notes: ["Adapted from Waza think skill. Plan-only, no execution."],
    },
    {
        id: "check",
        displayName: "Smart Shikka AI workflow skill: Check",
        description: "Reviews code diffs, PRs, issue queues, release readiness, and project audits.",
        skillFileName: "check-SKILL.md",
        relativePath: "packages/core/src/waza-workflow-skills/references/check-SKILL.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        planOnly: true,
        runtimeExecutionAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        referenceFiles: [
            "check-persona-catalog.md",
            "check-project-context.md",
            "check-public-reply.md",
        ],
        notes: ["Adapted from Waza check skill. Plan-only, no execution."],
    },
    {
        id: "hunt",
        displayName: "Smart Shikka AI workflow skill: Hunt",
        description: "Finds root cause before applying fixes for errors, crashes, and regressions.",
        skillFileName: "hunt-SKILL.md",
        relativePath: "packages/core/src/waza-workflow-skills/references/hunt-SKILL.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        planOnly: true,
        runtimeExecutionAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        referenceFiles: [
            "hunt-failure-patterns.md",
            "hunt-ime-unicode.md",
            "hunt-logging-techniques.md",
            "hunt-rendering-debug.md",
        ],
        notes: ["Adapted from Waza hunt skill. Plan-only, no execution."],
    },
    {
        id: "read",
        displayName: "Smart Shikka AI workflow skill: Read",
        description: "Reads URLs and PDFs by fetching source content with summaries or Markdown.",
        skillFileName: "read-SKILL.md",
        relativePath: "packages/core/src/waza-workflow-skills/references/read-SKILL.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        planOnly: true,
        runtimeExecutionAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        referenceFiles: [
            "read-methods.md",
        ],
        notes: ["Adapted from Waza read skill. Plan-only, no execution. Fetch scripts NOT copied."],
    },
    {
        id: "health",
        displayName: "Smart Shikka AI workflow skill: Health",
        description: "Agent-assisted engineering health audit for instruction/config drift.",
        skillFileName: "health-SKILL.md",
        relativePath: "packages/core/src/waza-workflow-skills/references/health-SKILL.md",
        sourceRepoUrl: SOURCE_REPO_URL,
        sourceCommitSha: SOURCE_COMMIT_SHA,
        sourceLicense: SOURCE_LICENSE,
        sourceLicenseVerified: true,
        planOnly: true,
        runtimeExecutionAllowed: false,
        blockedActions: COMMON_BLOCKED_ACTIONS,
        referenceFiles: [],
        notes: ["Adapted from Waza health skill. Plan-only, no execution."],
    },
]);
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const WAZA_WORKFLOW_SKILL_REGISTRY = deepFreeze({
    version: "1.0.0",
    generatedInPr: 79,
    sourceRepoUrl: SOURCE_REPO_URL,
    sourceCommitSha: SOURCE_COMMIT_SHA,
    sourceLicense: SOURCE_LICENSE,
    sourceLicenseVerified: true,
    skills: WAZA_SKILLS,
});
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function getWazaWorkflowSkills() {
    return WAZA_WORKFLOW_SKILL_REGISTRY.skills;
}
export function getWazaWorkflowSkillById(id) {
    return WAZA_WORKFLOW_SKILL_REGISTRY.skills.find((s) => s.id === id);
}
//# sourceMappingURL=wazaWorkflowSkillRegistry.js.map